import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { type User as UserEntity } from "wasp/entities";
import { SidebarBottomSection } from "../AppSidebar/SidebarBottomSection";
import EdgyDrawer from "../../../edgy/EdgyDrawer";
import NotificationsBell from "../../../notifications/NotificationsBell";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import useTranslation from "../../hooks/useTranslation";
import { cn } from "../../utils";
import {
  getModules,
  moduleForPath,
  getRememberedTab,
  canAccessMentor,
  type ModuleId,
} from "./navConfig";

/**
 * Level 1: the thin dark rail holding the four modules.
 *
 * Plan-gated modules stay visible with a lock rather than disappearing — a hidden
 * feature can't be discovered, and this is the upgrade surface.
 */

const RAIL_WIDTH = 60;

export default function PrimaryRail({
  user,
}: {
  user: Partial<UserEntity> & { isAdmin?: boolean };
}) {
  const userPlan = user.subscriptionPlan;
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEdgyOpen, setIsEdgyOpen] = useState(false);

  const modules = getModules(t);
  const activeModule: ModuleId = moduleForPath(location.pathname, modules);
  const mentorUnlocked = canAccessMentor(userPlan);

  function openModule(moduleId: ModuleId, defaultPath: string, locked: boolean) {
    if (locked) {
      // Send the trader to the pricing page rather than silently doing nothing.
      navigate("/pricing");
      return;
    }
    // Returning to a module restores the tab it was left on.
    navigate(getRememberedTab(moduleId) ?? defaultPath);
  }

  return (
    <>
    <nav
      aria-label={t.newNav.menu}
      className="hidden lg:flex sticky top-0 z-40 h-screen shrink-0 flex-col items-center justify-between bg-navRail py-3"
      style={{ width: RAIL_WIDTH }}
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex flex-col items-center gap-1">
          {/* Logo — typographic, per the Edgely brand rules. */}
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="mb-2 flex h-10 w-10 items-center justify-center"
            aria-label="Edgely"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <span className="text-2xl font-bold text-white">
              e<span className="text-navRail-accent">.</span>
            </span>
          </button>

          {modules.map((module) => {
            const Icon = module.icon;
            const isActive = activeModule === module.id;
            const locked = module.requiresPlan === true && !mentorUnlocked;

            return (
              <Tooltip key={module.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => openModule(module.id, module.defaultPath, locked)}
                    aria-label={module.label}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
                      // Active module: lighter emerald block, matching the mockup.
                      isActive ? "bg-navRail-active text-white" : "hover:bg-white/10",
                      !isActive && (locked ? "text-white/40" : "text-white/80 hover:text-white")
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {locked && (
                      <Lock className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-navRail p-[1px]" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {module.label}
                  {locked && <span className="block text-xs opacity-70">{t.newNav.lockedTooltip}</span>}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="flex w-full flex-col items-center gap-2">
          {/* Edgy. It lived in AppSidebar, which this rail replaces, so without it
              here an analysis could not be launched from anywhere. Kept in the rail
              rather than floating over the page: the bottom corners are taken (the
              support launcher on the right, these controls on the left). */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setIsEdgyOpen(true)}
                aria-label={t.edgy.buttonLabel}
                className="group relative flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-[0_0_0_0_rgba(16,185,129,0.5)] transition-shadow duration-300 hover:shadow-[0_0_16px_2px_rgba(16,185,129,0.45)]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-lg bg-emerald-400/30 opacity-0 motion-safe:animate-pulse group-hover:opacity-100"
                />
                <Sparkles className="relative h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="whitespace-nowrap">
              {t.edgy.buttonLabel}
            </TooltipContent>
          </Tooltip>

          {/* Cloche de notifications. Sous Edgy, dans le groupe bas : c'est une
              surface de consultation, pas un module de navigation. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <NotificationsBell />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="whitespace-nowrap">
              {t.notificationsCenter.title}
            </TooltipContent>
          </Tooltip>

          {/* Theme / language / Discord / profile — reuses the existing bottom section
              instead of reimplementing four dropdowns. Collapsed: the rail is 60px
              wide, so the expanded variant's text labels ("Tokyo Night", "Français")
              wrap onto two lines and spill past its edge. */}
          <div className="w-full">
            <SidebarBottomSection isCollapsed user={user} />
          </div>
        </div>
      </TooltipProvider>
    </nav>

    {/* Outside the <nav>: the rail is a 60px sticky column, so a panel rendered
        inside it would be clipped by its width and stacking context. */}
    <EdgyDrawer open={isEdgyOpen} onClose={() => setIsEdgyOpen(false)} />
    </>
  );
}
